import React, { useEffect, useState, useRef } from 'react';
import Layout from 'layout/layout';
import { FiArrowDownLeft, FiArrowUpRight } from 'react-icons/fi';
import { PiPaperPlaneTiltLight } from 'react-icons/pi';
import { TbArrowsExchange, TbFileInvoice } from 'react-icons/tb';
import { CgFileDocument } from 'react-icons/cg';
import { Form, OverlayTrigger, Spinner, Table, Tooltip } from 'react-bootstrap';
import '../styles/wallet.css';
import { useCorporateWalletBalanceQuery, usePackagesWalletBalanceQuery, useWalletBalanceHistoryQuery } from 'api/walletApi';
import { CorporateWalletBalance, WalletBalanceHistory, WalletHistoryQuery, WalletPaginationItem } from 'model/wallect-model';
import { showToastError } from 'components/toastify/ToastNotifications';
import { FormattedDateOnly } from 'components/formatted-date';
import Pagination from 'rc-pagination/lib/Pagination';
import dayjs from 'dayjs';
import { retrieveUserData } from 'redux/authSlice';
import { useGetPermissionsByIdQuery } from 'api/userManagementApi';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { BiChevronLeft, BiChevronLeftCircle, BiChevronRight, BiChevronRightCircle } from 'react-icons/bi';
import ModalComponent from 'components/modal-component';
import { AiOutlineClose } from 'react-icons/ai';
import Logo from '../assets/images/logo.svg';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

type Props = {}

const Wallet: React.FC = (props: Props) => {

  const userRoleId = retrieveUserData()?.roleId || '';

  const { data: permissionsData, isFetching: fetchingPermission, refetch: refetchUserPermission } = useGetPermissionsByIdQuery(userRoleId, { skip: userRoleId ? false : true });
  useEffect(() => {
    if (permissionsData?.data) {
      refetchUserPermission();
    }
  }, []);
  const userPermission = permissionsData?.data && permissionsData?.data?.permissions?.includes("View Wallet");

  const [activePlan, setActivePlan] = useState("All");
  const [viewBalances, setViewBalances] = useState(true);
  const [selectPackage, setSelectPackage] = useState<number>()

  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [corporateWallet, setCorporateWallet] = useState<CorporateWalletBalance>();
  const { data: getCorporateWalletBalance, error: getCorporateWalletBalanceError, isFetching: getCorporateWalletBalanceisFetching } = useCorporateWalletBalanceQuery();

  useEffect(() => {
    if (getCorporateWalletBalanceisFetching) {
      setLoading(true);
    } else {
      setLoading(false);
      if (getCorporateWalletBalanceError) {
        showToastError("Unable to fetch data from the server");
      } else {
        setCorporateWallet(getCorporateWalletBalance?.data as CorporateWalletBalance);
      }
    }
  }, [getCorporateWalletBalanceisFetching]);

  const [walletQuery, setWalletQuery] = useState<WalletHistoryQuery>({
    page: 1,
    pageSize: 10,
    application: 'Joble'
  })

  const onPageChange = (page: number) => {
    setWalletQuery((prev) => ({ ...prev, page: page }));
    // You can perform actions like fetching data for the new page here
  };

  const [walletHistory, setWalletHistory] = useState<WalletPaginationItem>();
  const [walletHistoryFilter, setWalletHistoryFilter] = useState<WalletBalanceHistory[]>([]);
  const { data: getWalletHistory, error: getWalletHistoryError, isFetching: getWalletHistoryisFetching, refetch: refetchWalletHistory } = useWalletBalanceHistoryQuery(walletQuery);

  useEffect(() => {
    if (getWalletHistoryisFetching) {
      setLoadingHistory(true);
    } else {
      setLoadingHistory(false);
      if (getWalletHistoryError) {
        showToastError("Unable to fetch data from the server");
      } else {
        setWalletHistory(getWalletHistory?.data as WalletPaginationItem);
        setWalletHistoryFilter(getWalletHistory?.data?.items as WalletBalanceHistory[]);
      }
    }
  }, [getWalletHistoryisFetching, walletQuery]);

  useEffect(()=> {
    refetchWalletHistory();
  }, [walletQuery.page])

  const { data: allPackageData, error: allPackageDataError } = usePackagesWalletBalanceQuery();

  const handleFilter = (data: string) => {
    if (data === 'Credit') {
      setActivePlan('Credit')
      setWalletHistoryFilter(walletHistory?.items?.filter(item => item?.transactionType === 'Credit') || [])
    }
    else if (data === 'Debit') {
      setActivePlan('Debit')
      setWalletHistoryFilter(walletHistory?.items?.filter(item => item?.transactionType === 'Debit') || [])
    }
    else {
      setActivePlan('All')
      setWalletHistoryFilter(walletHistory?.items || [])
    }
  }

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    prevArrow: (
      <BiChevronLeftCircle size={50} color='#818181' className="p-3 fs-3 slick-prev" />
    ),
    nextArrow: (
      <BiChevronRightCircle size={50} color='#818181' className="p-3 fs-3 slick-next" />
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const [invoice, setInvoice] = useState<WalletBalanceHistory>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleViewInvoice = (item: WalletBalanceHistory) => {
    setInvoice(item);
    setIsModalOpen(true);
  }

  const divRef = useRef<HTMLDivElement>(null);

  const handleDownloadPdf = async () => {
    if (!divRef.current) return;

    // Capture the div as an image using html2canvas
    const canvas = await html2canvas(divRef.current);

    // Convert the canvas to an image
    const imgData = canvas.toDataURL('image/png');

    // Create a PDF with jsPDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [canvas.width, canvas.height],
    });

    // Add the image to the PDF
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);

    // Download the PDF
    pdf.save(`${invoice?.companyName}-invoice.pdf`);
  };


  return (
    <Layout>
      <section className='my-4'>
        {loading ? <div className='p-5 text-center'><Spinner /> </div> : <div className='row'>
          <div className='col-lg-6'>
            <div className='wallet-blue-card text-white d-flex justify-content-between p-4 rounded-5'>
              <div className='mt-3'>
                <h6>Corporate Wallet Available Balance</h6>
                <span>Funds comes to this wallet </span>
                <h2>€{corporateWallet?.moneyIn?.toLocaleString()}</h2>
              </div>
              <div className='text-end'>
                <button className='btn-light-blue font-size-sm'><FiArrowDownLeft size={20} /> Money in</button>
              </div>
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='wallet-green-card text-white d-flex justify-content-between p-4 rounded-5'>
              <div className='mt-3'>
                <h6>Corporate Wallet Available Balance</h6>
                <span>Funds goes out of this wallet </span>
                <h2>€{corporateWallet?.moneyOut?.toLocaleString()}</h2>
              </div>
              <div className='text-end'>
                <button className='btn-light-green font-size-sm'><FiArrowUpRight size={20} /> Money out</button>
              </div>
            </div>
          </div>
        </div>}

        <div className='my-4'>
          <h6 className='mb-3'>Quick Links</h6>
          <div className='d-flex gap-3'>
          <OverlayTrigger
              placement="right"
              delay={{ show: 250, hide: 400 }}
              overlay={<Tooltip id="button-tooltip" {...props}>
                Coming soon
              </Tooltip>}
            >
              <button className='btn-secondary-outline w-25 py-1'><PiPaperPlaneTiltLight size={25} className='me-3 align-auto' /> Withdraw funds</button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="right"
              delay={{ show: 250, hide: 400 }}
              overlay={<Tooltip id="button-tooltip" {...props}>
                Coming soon
              </Tooltip>}
            >
              <button className='btn-secondary-outline w-25 py-1'><TbArrowsExchange size={25} className='me-3 exchangeIcon align-auto' /> View All Transactions</button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="right"
              delay={{ show: 250, hide: 400 }}
              overlay={<Tooltip id="button-tooltip" {...props}>
                Coming soon
              </Tooltip>}
            >
              <button className='btn-secondary-outline w-25 py-1'><CgFileDocument size={25} className='me-3 align-auto' /> Download Statement</button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="right"
              delay={{ show: 250, hide: 400 }}
              overlay={<Tooltip id="button-tooltip" {...props}>
                Coming soon
              </Tooltip>}
            >
              <button className='btn-secondary-outline w-25 py-1'><TbFileInvoice size={25} className='me-3 align-auto' /> Failed Payments</button>
            </OverlayTrigger>
          </div>
        </div>

        <div><button type='button' onClick={() => setViewBalances(!viewBalances)} className='btn-secondary-outline w-100'>{viewBalances ? 'Hide' : 'View all'} wallet balances</button></div>
        {viewBalances &&
          <div className="py-3">
            <Slider {...sliderSettings}>
              {allPackageData?.data?.map((item, key) => (
                <div key={key} className='px-2'>
                  <div
                    onMouseOver={() => setSelectPackage(key)}
                    onMouseOut={() => setSelectPackage(undefined)}
                    onClick={() =>
                      setWalletQuery((prevQuery) => ({
                        ...prevQuery,
                        application: item?.package,
                      }))
                    }
                    className={`card pointer btn text-start px-3 py-2 border-0 rounded-3 ${walletQuery.application === item?.package ? 'bg-primary-outline' : 'bg-outline-secondary'
                      }`}
                  >
                    <span className="font-size-sm text-secondary">
                      {item?.package} Wallet Balance
                    </span>
                    <h4 className="fw-bold my-1 text-secondary">
                      €{item?.amount.toLocaleString() || 0}.00
                    </h4>
                    <div
                      className={`${selectPackage === key ? 'text-primary' : 'text-secondary'
                        } py-1 me-auto font-size-sm`}
                    >
                      Go to wallet
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        }

        {loadingHistory ? <div className='p-5 text-center'><Spinner /></div> : <div className='my-4'>
          <h6 className='fw-bold'>Transaction History</h6>
          <div hidden={walletHistoryFilter?.length < 1 && walletQuery.application == undefined} className='row pt-2 pb-4'>
            <div className='col-md-5 pt-3'>
              <button type='button' onClick={() => handleFilter('All')} className={activePlan === "All" ? "btn-primary-outline font-size-sm first-font-medium px-3 me-3" : "btn-secondary-outline font-size-sm first-font-medium px-3 me-3"}>All</button>
              <button type='button' onClick={() => handleFilter('Credit')} className={activePlan === "Credit" ? "btn-primary-outline font-size-sm first-font-medium px-3 me-3" : "btn-secondary-outline font-size-sm first-font-medium px-3 me-3"}>Credit History</button>
              <button type='button' onClick={() => handleFilter('Debit')} className={activePlan === "Debit" ? "btn-primary-outline font-size-sm first-font-medium px-3 me-3" : "btn-secondary-outline font-size-sm first-font-medium px-3 me-3"}>Debit History</button>
            </div>
            <div className='col-lg-7 row'>
              <div className='col-lg-3'>
                <label className='font-size-smm'>From</label>
                <Form.Control type='date' value={dayjs(walletQuery?.fromDate).format('YYYY-MM-DD')} onChange={(e) => setWalletQuery({ ...walletQuery, fromDate: dayjs(e.target.value).format('YYYY-MM-DD') })} aria-label="Floating label select example" className="bg-light border-1 w-100">
                </Form.Control>
              </div>
              <div className='col-lg-3'>
                <label className='font-size-smm'>To</label>
                <Form.Control type='date' value={dayjs(walletQuery?.toDate).format('YYYY-MM-DD')} onChange={(e) => setWalletQuery({ ...walletQuery, toDate: dayjs(e.target.value).format('YYYY-MM-DD') })} aria-label="Floating label select example" className="bg-light border-1 w-100">
                </Form.Control>
              </div>
              <div className='col-lg-3'>
                <label className='font-size-smm'>Provider</label>
                <Form.Select className='bg-light' value={walletQuery?.provider} onChange={(e) => {
                  const selectedProvider = e.target.value;
                  setWalletQuery({
                    ...walletQuery,
                    provider: selectedProvider === '' ? undefined : selectedProvider
                  });
                }}>
                  <option value=''>All</option>
                  <option value='Mollie'>Mollie</option>
                  <option value='Stripe'>Stripe</option>
                </Form.Select>
              </div>
              <div className='col-lg-3'>
                <label className='font-size-smm'>Sort by</label>
                <Form.Select className='bg-light' value={walletQuery?.sortBy} onChange={(e) => {
                  const selectedProvider = e.target.value;
                  setWalletQuery({ ...walletQuery, sortBy: selectedProvider === '' ? undefined : selectedProvider })
                }}>
                  <option value='Plan'>Plan</option>
                  <option value='CompanyName'>CompanyName</option>
                  <option value='Application'>Application</option>
                  <option value='Provider'>Provider</option>
                  <option value='Amount'>Amount</option>
                </Form.Select>
              </div>
              {/* <div className='col-lg-2'><button type='button' onClick={()=> getActivityLogs()} className='btn btn-primary py-3 px-4'>View</button></div> */}

            </div>
          </div>
          {walletHistoryFilter && walletHistoryFilter?.length > 0 ? <> <Table responsive className='table'>
            <thead>
              <tr>
                <th>
                  <label className='fw-normal'>SN</label>
                </th>
                <th>
                  <label className='fw-normal'>Company Name</label>
                </th>
                <th>
                  <label className='fw-normal'>Date</label>
                </th>
                <th>
                  <label className='fw-normal'>Trans Type</label>
                </th>
                <th>
                  <label className='fw-normal'>Provider</label>
                </th>
                <th><label className='fw-normal'>Package</label>
                </th>
                <th><label className='fw-normal'>Plan</label>
                </th>
                <th><label className='fw-normal'>Amount</label>
                </th>
                <th><label className='fw-normal'>Action</label>
                </th>
              </tr>
            </thead>
            <tbody>
              {walletHistoryFilter?.map((item, key) => <tr key={key} className='align-middle'>
                <td>{((walletQuery?.page - 1) * walletQuery?.pageSize) + key + 1}</td>
                <td>{item?.companyName}</td>
                <td><span className='text-secondary'>{item?.transactionDate && FormattedDateOnly(item?.transactionDate)}</span></td>
                <td><span className='text-secondary'>{item?.transactionType}</span></td>
                <td><span className='text-secondary'>{item?.paymentProviders}</span></td>
                <td><span className='text-secondary'>{item?.package}</span></td>
                <td><span className='text-secondary'>{item?.plans}</span></td>
                <td><span className='text-secondary'>€{item?.amount?.toLocaleString()}</span></td>
                <td><button type='button' onClick={() => handleViewInvoice(item)} className='btn-primary-outline px-2 py-1 rounded-3'>View Invoice</button></td>
              </tr>)}
            </tbody>
          </Table>
            <div className='text-end py-3'>
              <Pagination
                current={walletHistory?.pageNumber || 0}
                total={walletHistory?.totalSize}
                pageSize={walletHistory?.pageSize}
                onChange={onPageChange}
              />
            </div>
          </>
            : <div className='p-5 text-center'> <span>No wallet history found</span> </div>}
        </div>}

        {/* <div><button className='btn-secondary-outline w-100'>Hide</button></div> */}

        <ModalComponent size='lg' showModal={isModalOpen} hideModal={() => setIsModalOpen(false)}>
          <div className='d-flex justify-content-between gap-3 px-3 pt-2'>
          <button type='button' className='btn-primary-outline px-2 py-1' onClick={handleDownloadPdf}>
            Download as PDF
          </button>
            <button className='btn p-0'><AiOutlineClose onClick={() => setIsModalOpen(false)} size={20} /></button>
          </div>
          <div ref={divRef} className='p-5 bg-white'>
            <div className='d-flex justify-content-between'>
              <div className=''>
                <h1 className='fs-1 fw-bold'>INVOICE</h1>
                <p className='d-flex gap-4'>
                  <label className='dark-text font-size-smm'>INVOICE NUMBER <span className='d-block darker-text fw-bold font-size-smm'>000000827</span></label>
                  <label className='dark-text font-size-smm'>DATE OF ISSUE <span className='d-block darker-text fw-bold font-size-smm'>{invoice?.transactionDate && FormattedDateOnly(invoice?.transactionDate)}</span></label>
                </p>
              </div>
              <div>
                <img src={Logo} width="150px" alt='JobPro' />
              </div>
            </div>

            <div className='mt-4 d-flex justify-content-between'>
              <p>
                <span className='d-block primary-text fw-bold font-size-smm pb-2'>BILLED TO</span>
                <span className='d-block text-darker fw-bold font-size-smm'>{invoice?.companyName}</span>
                <span className='d-block text-darker fw-bold font-size-smm py-1'>info@company.com</span>
              </p>
              <p>
                <span className='d-block primary-text fw-bold font-size-smm pb-2'>JobPro Suite</span>
                <span className='d-block text-darker fw-bold font-size-smm py-1'>invoice@jobpro.app</span>
                <span className='d-block text-darker fw-bold font-size-smm'>www.jobpro.app</span>
              </p>
            </div>
            <div className=''><hr className='bg-primary primary-text fw-bold border-none' style={{ height: '2px', opacity: 1 }} /></div>
            <div className='mt-3'>
              <Table responsive className='invoiceTable table w-100'>
                <thead>
                  <tr>
                    <th><span className='dark-text font-size-smm'>NAME OF PACKAGE</span></th>
                    <th><span className='dark-text font-size-smm'>NAME OF PLAN</span></th>
                    {/* <td><span className='dark-text font-size-smm'>NO OF USERS</span></td> */}
                    <th className='text-end'><span className='dark-text font-size-smm'>AMOUNT PAID</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className='d-block darker-text fw-bold font-size-smm'>{invoice?.package}</span></td>
                    <td><span className='d-block darker-text fw-bold font-size-smm'>{invoice?.plans}</span></td>
                    {/* <td><span className='d-block darker-text fw-bold font-size-smm'>15</span></td> */}
                    <td className='text-end'><span className='d-block darker-text fw-bold font-size-smm'>€{invoice?.amount?.toLocaleString()}.00</span></td>
                  </tr>
                </tbody>
              </Table>
            </div>
            <div className=''><hr className='bg-secondary text-secondary fw-bold border-none' style={{ height: '2px', opacity: 1 }} /></div>
            <div className='mt-4 d-flex justify-content-between'>
              <p>
                <span className='d-block primary-text fw-bold font-size-smm'>INVOICE TOTAL</span>
                <span className='d-block text-darker fw-bold fs-2'>€{invoice?.amount?.toLocaleString()}.00</span>
              </p>
              <p>
                <label className='d-block d-flex justify-content-between dark-text font-size-smm'>Subtotal <span className='ps-5 darker-text fw-bold font-size-smm'>€{invoice?.amount?.toLocaleString()}.00</span></label>
                <label className='d-block d-flex justify-content-between dark-text font-size-smm'>VAT <span className='ps-5 darker-text fw-bold font-size-smm'>0.00</span></label>
                <label className='d-block d-flex justify-content-between dark-text font-size-smm'>Total Due <span className='ps-5 darker-text fw-bold font-size-smm'>€{invoice?.amount?.toLocaleString()}.00</span></label>
              </p>
            </div>
          </div>
        </ModalComponent>
      </section>
    </Layout >
  )
}

export default Wallet;